(function () {
  'use strict';

  // Cart drawer conversion messaging.
  //
  // 1) "You're getting" box near the Subtotal: gift value + the REAL
  //    new/returning customer saving read live from the cart ($30 or $40),
  //    closed by a "Total savings today" line — the single anchor number.
  // 2) Struck-through TOTAL VALUE beside the Subtotal = sum of every line's
  //    ORIGINAL price, so it matches the PDP card's "Total value".
  // 3) Replaces a cosmetic "-$0.00" gift label on the program line.
  //
  // The "New/Returning Customer Offer" line label is left alone on purpose —
  // it's the customer's proof the discount applied. Rename it in Admin.
  //
  // The savings total is deliberately rendered INSIDE our own #fitaz-gift-savings
  // block rather than as a new row next to the theme's Subtotal. Anchoring a new
  // row would mean matching money strings in the drawer's own markup, and the
  // program line and the Subtotal can hold the SAME value (e.g. both "$256.00"),
  // so a text match cannot reliably tell them apart. Owning the container by id
  // avoids that class of bug entirely.
  //
  // PERFORMANCE NOTES (this file used to be a hot loop):
  //  - We write to the DOM, which fires our own MutationObserver, which
  //    scheduled another pass, which wrote again. The rAF guard capped it at
  //    one pass per FRAME, not one pass per change — up to 60 full-document
  //    scans a second on a long PDP. `writing` now suppresses self-triggered
  //    passes, which is the single biggest win here.
  //  - characterData:true fired on every text node mutation anywhere on the
  //    page (timers, carousels, video captions). Removed — childList is enough.
  //  - findSubtotalLeaf() scanned every div/span/p/dt/td/strong in the document
  //    on every pass. The result is now cached and only re-queried when the
  //    cached node leaves the DOM or goes invisible (i.e. drawer re-render).
  //  - Once the Subtotal is known we scope further queries to its container.
  //  - Passes are debounced (~100ms) and skipped when the cart is unchanged.

  var NEW_CUSTOMER_MIN_CENTS = 8900;
  var CHECKOUT_MESSAGE = 'Get $30 off at checkout';
  var PROGRAM_LINE_NUDGE = 'Checkout to get additional $30 off';
  var GIFT_LABEL = 'Free HIIT Pilates included';
  var SAVINGS_LABEL = 'Total savings today';
  var GIFT_TITLE_RE = /free hiit pilates with program/i;
  var BLOCK_ID = 'fitaz-gift-savings';
  var ACCENT = '#ed4281';
  var DEBOUNCE_MS = 100;

  var giftVariantId = (function () {
    try {
      var el = document.getElementById('ea-preload-config');
      if (!el) return null;
      var cfg = JSON.parse(el.textContent || '');
      return cfg && cfg.giftVariantId ? String(cfg.giftVariantId) : null;
    } catch (e) {
      return null;
    }
  })();

  var money = function (cents) {
    return '$' + (Math.round(cents) / 100).toFixed(2);
  };

  var norm = function (el) {
    return (el.textContent || '').replace(/\s+/g, ' ').trim();
  };

  var lastCart = null;
  var lastFetch = 0;
  var fetching = false;
  var writing = false;
  var timer = null;
  var lastKey = '';

  var refreshCart = function () {
    var now = Date.now();
    if (fetching || now - lastFetch < 600) return;
    fetching = true;
    lastFetch = now;
    fetch('/cart.js', { headers: { Accept: 'application/json' }, credentials: 'same-origin' })
      .then(function (r) { return r.json(); })
      .then(function (cart) { lastCart = cart; fetching = false; schedule(); })
      .catch(function () { fetching = false; });
  };

  var readCart = function (cart) {
    var out = { giftPresent: false, giftSavings: 0, otherSavings: 0, totalOriginal: 0 };
    if (!cart || !cart.items) return out;
    cart.items.forEach(function (it) {
      var original = it.original_line_price != null ? it.original_line_price : it.original_price;
      var final = it.final_line_price != null ? it.final_line_price : it.final_price;
      var saved = Math.max(0, original - final);
      out.totalOriginal += original;

      var isGift =
        (giftVariantId && String(it.variant_id) === giftVariantId) ||
        (it.properties && it.properties._free_gift);

      if (isGift) {
        out.giftPresent = true;
        out.giftSavings += saved;
      } else {
        out.otherSavings += saved;
      }
    });
    return out;
  };

  var subtotalOf = function (cart) {
    if (!cart) return 0;
    return cart.items_subtotal_price != null ? cart.items_subtotal_price : cart.total_price;
  };

  var cartKey = function (cart) {
    if (!cart) return '';
    var k = String(subtotalOf(cart)) + '|' + String(cart.item_count);
    (cart.items || []).forEach(function (it) {
      k += '|' + it.variant_id + ':' + it.quantity + ':' +
           (it.final_line_price != null ? it.final_line_price : it.final_price);
    });
    return k;
  };

  // ---- Subtotal anchor (cached) ----------------------------------------------

  var cachedLeaf = null;
  var cachedScope = null;

  var leafIsUsable = function (n) {
    return !!(n && n.isConnected && n.offsetParent !== null);
  };

  var findSubtotalLeaf = function () {
    if (leafIsUsable(cachedLeaf)) return cachedLeaf;
    cachedLeaf = null;
    cachedScope = null;

    var nodes = document.body.querySelectorAll('div, span, p, dt, td, strong');
    for (var i = 0; i < nodes.length; i++) {
      var n = nodes[i];
      if (n.childElementCount > 0) continue;
      if (/^subtotal$/i.test(norm(n)) && n.offsetParent !== null) {
        cachedLeaf = n;
        var scope = n;
        for (var d = 0; d < 6 && scope.parentElement; d++) scope = scope.parentElement;
        cachedScope = scope;
        return cachedLeaf;
      }
    }
    return null;
  };

  var scopeEl = function () {
    return (cachedScope && cachedScope.isConnected) ? cachedScope : document.body;
  };

  // ---- 1) Savings box ---------------------------------------------------------

  var buildBlock = function (cart) {
    var info = readCart(cart);
    var applied = info.otherSavings > 0;
    var showCheckout = !applied && subtotalOf(cart) >= NEW_CUSTOMER_MIN_CENTS;
    if (!info.giftPresent && !applied && !showCheckout) return null;

    var rows = '';
    if (info.giftPresent) {
      var valueText = info.giftSavings > 0 ? ' \u2014 ' + money(info.giftSavings) + ' value' : '';
      rows +=
        '<div style="display:flex;align-items:center;gap:8px;">' +
        '<span aria-hidden="true">\uD83C\uDF81</span>' +
        '<span>' + GIFT_LABEL + valueText + '</span></div>';
    }
    if (applied) {
      rows +=
        '<div style="display:flex;align-items:center;gap:8px;' + (info.giftPresent ? 'margin-top:6px;' : '') + '">' +
        '<span aria-hidden="true">\uD83D\uDCB3</span>' +
        '<span><b>' + money(info.otherSavings) + ' off</b> applied today</span></div>';
    } else if (showCheckout) {
      rows +=
        '<div style="display:flex;align-items:center;gap:8px;' + (info.giftPresent ? 'margin-top:6px;' : '') + '">' +
        '<span aria-hidden="true">\uD83D\uDCB3</span>' +
        '<span>' + CHECKOUT_MESSAGE + '</span></div>';
    }

    // The anchor: everything above, added up, in one number. Only shown when
    // there's more than one saving to add up — otherwise it just restates the
    // single line above it.
    var totalSaved = info.giftSavings + info.otherSavings;
    var multiple = info.giftSavings > 0 && info.otherSavings > 0;
    if (totalSaved > 0 && multiple) {
      rows +=
        '<div style="display:flex;align-items:baseline;justify-content:space-between;gap:10px;' +
        'margin-top:9px;padding-top:9px;border-top:1px solid ' + ACCENT + '33;">' +
        '<span style="font-weight:700;color:' + ACCENT + ';">' + SAVINGS_LABEL + '</span>' +
        '<span style="font-weight:700;font-size:15px;color:' + ACCENT + ';">' + money(totalSaved) + '</span>' +
        '</div>';
    }

    var wrap = document.createElement('div');
    wrap.id = BLOCK_ID;
    wrap.style.cssText =
      'margin:12px 0;padding:12px 14px;border-radius:12px;' +
      'background:#fdf1f5;color:#3a2a2f;font-size:13px;line-height:1.35;' +
      'border:1px solid ' + ACCENT + '22;';
    wrap.innerHTML =
      '<div style="font-weight:700;color:' + ACCENT + ';margin-bottom:6px;">You\u2019re getting</div>' + rows;
    return wrap;
  };

  var injectSavings = function (cart) {
    var block = buildBlock(cart);
    var existing = document.getElementById(BLOCK_ID);
    if (!block) { if (existing) existing.remove(); return; }

    var leaf = findSubtotalLeaf();
    if (!leaf) { if (existing) existing.remove(); return; }
    var row = leaf.parentElement || leaf;

    if (existing) {
      if (existing.nextSibling === row && existing.parentNode === row.parentNode) {
        if (existing.innerHTML !== block.innerHTML) existing.innerHTML = block.innerHTML;
        return;
      }
      existing.remove();
    }
    if (row.parentNode) row.parentNode.insertBefore(block, row);
  };

  // ---- 2) Struck TOTAL VALUE --------------------------------------------------

  var moneyLeafRe = /^\$[\d,]+\.\d{2}$/;

  var decorateSubtotal = function (cart) {
    var info = readCart(cart);
    var leaf = findSubtotalLeaf();
    if (!leaf) return;
    var row = leaf.parentElement;
    if (!row) return;

    var amountLeaf = null;
    var kids = row.querySelectorAll('div, span, p, dd, td, strong');
    for (var i = 0; i < kids.length; i++) {
      if (kids[i].childElementCount === 0 && moneyLeafRe.test(norm(kids[i]))) { amountLeaf = kids[i]; break; }
    }
    if (!amountLeaf) return;

    var existing = row.querySelector('[data-fitaz-orig]');
    var original = info.totalOriginal;
    if (original <= subtotalOf(cart)) { if (existing) existing.remove(); return; }

    var text = money(original);
    if (existing) { if (existing.textContent !== text) existing.textContent = text; return; }

    var strike = document.createElement('span');
    strike.setAttribute('data-fitaz-orig', '1');
    strike.style.cssText = 'text-decoration:line-through;opacity:.5;font-weight:400;margin-right:8px;';
    strike.textContent = text;
    amountLeaf.parentNode.insertBefore(strike, amountLeaf);
  };

  // ---- 3) "-$0.00" gift label -------------------------------------------------

  var replaceZeroGiftLabels = function () {
    var root = scopeEl();
    var all = root.querySelectorAll('div, span, p, li, dd, small');
    var matches = [];
    for (var i = 0; i < all.length; i++) {
      var n = all[i];
      if (n.getAttribute('data-fitaz-nudge') === '1') continue;
      var t = norm(n);
      if (!GIFT_TITLE_RE.test(t)) continue;
      if (!/\$0\.00/.test(t)) continue;
      if (/\$[1-9]/.test(t)) continue;
      matches.push(n);
    }
    if (!matches.length) return;
    matches.forEach(function (n) {
      var hasInner = matches.some(function (m) { return m !== n && n.contains(m); });
      if (hasInner) return;
      n.textContent = PROGRAM_LINE_NUDGE;
      n.setAttribute('data-fitaz-nudge', '1');
      n.style.color = ACCENT;
      n.style.fontWeight = '600';
    });
  };

  // ---- Orchestration ----------------------------------------------------------

  var run = function () {
    timer = null;
    if (!lastCart) return;

    var key = cartKey(lastCart);
    var blockOk = !!document.getElementById(BLOCK_ID) || !leafIsUsable(cachedLeaf);
    if (key === lastKey && blockOk && leafIsUsable(cachedLeaf)) return;

    writing = true;
    try {
      replaceZeroGiftLabels();
      injectSavings(lastCart);
      decorateSubtotal(lastCart);
      lastKey = key;
    } catch (e) { /* fail safe */ }
    setTimeout(function () { writing = false; }, 0);
  };

  var schedule = function () {
    if (timer) return;
    timer = setTimeout(run, DEBOUNCE_MS);
  };

  var onMutation = function () {
    if (writing) return;
    schedule();
    refreshCart();
  };

  var start = function () {
    refreshCart();
    schedule();
    new MutationObserver(onMutation).observe(document.body, {
      childList: true, subtree: true
    });
    document.addEventListener('cart:updated', refreshCart, true);
    document.addEventListener('cart:refresh', refreshCart, true);
    document.addEventListener('cart:build', refreshCart, true);
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start);
  } else {
    start();
  }
})();
